<template>
    <v-card flat>
        <v-overlay :value="loadingClientList">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
        <v-data-table
            :headers="tableHeaders"
            :items="filteredClients"
            :items-per-page="100"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :footer-props="{ 'items-per-page-options': [50, 100, 200, -1] }"
            dense
            fixed-header
            class="client-table"
            @click:row="handleRowClick"
            :item-class="getRowClass"
            ref="dataTable"
        >
            <!-- Id column -->
            <template v-slot:item.id="{ item }">
                <span class="id-cell">{{ item.id }}</span>
            </template>

            <!-- Name column -->
            <template v-slot:item.name="{ item }">
                <span :class="{ 'highlighted-cell': highlightedColumn === 'name' }">
                    {{ item.name || '-' }}
                </span>
            </template>

            <!-- Chain name column -->
            <template v-slot:item.chain_name="{ item }">
                <span :class="{ 'highlighted-cell': highlightedColumn === 'chain_name' }">
                    {{ item.chain_name || '-' }}
                </span>
            </template>

            <!-- Shop name column -->
            <template v-slot:item.shop_name="{ item }">
                <span :class="{ 'highlighted-cell': highlightedColumn === 'shop_name' }">
                    {{ item.shop_name || '-' }}
                </span>
            </template>

            <!-- Client country with flag -->
            <template v-slot:item.client_country="{ item }">
                <span :class="{ 'highlighted-cell': highlightedColumn === 'client_country' }">
                    <country-flag v-if="item.country_code" :country="item.country_code" size="small" class="mr-1" />
                    {{ item.country_english || item.country_estonian || '-' }}
                </span>
            </template>

            <!-- Shop country code with flag -->
            <template v-slot:item.shop_country_code="{ item }">
                <span :class="{ 'highlighted-cell': highlightedColumn === 'shop_country_code' }">
                    <country-flag v-if="item.shop_country_code" :country="item.shop_country_code" size="small" class="mr-1" />
                    {{ item.shop_country_code || '-' }}
                </span>
            </template>

            <!-- Odoo integration status -->
            <template v-slot:item.odoo="{ item }">
                <v-icon v-if="item.odoo" small color="green">mdi-check-circle</v-icon>
                <v-icon v-else small color="grey lighten-1">mdi-circle-outline</v-icon>
            </template>

            <!-- Eugesta integration status -->
            <template v-slot:item.eugesta="{ item }">
                <v-icon v-if="item.eugesta" small color="green">mdi-check-circle</v-icon>
                <v-icon v-else small color="grey lighten-1">mdi-circle-outline</v-icon>
            </template>

            <!-- Smarten integration status -->
            <template v-slot:item.smarten="{ item }">
                <v-icon v-if="item.smarten" small color="green">mdi-check-circle</v-icon>
                <v-icon v-else small color="grey lighten-1">mdi-circle-outline</v-icon>
            </template>

            <!-- Tags column -->
            <template v-slot:item.tags="{ item }">
                <span :class="{ 'highlighted-cell': highlightedColumn === 'tags' }">
                    <span v-for="tag in item.tags" :key="tag" class="tag-chip">
                        {{ tag }}
                    </span>
                    <span v-if="!item.tags || item.tags.length === 0">-</span>
                </span>
            </template>

        </v-data-table>
    </v-card>
</template>

<script>
import Store from "@/store";
import { mapState, mapGetters, mapMutations } from "vuex";
import CountryFlag from "vue-country-flag";
import { PermissionsConstants } from "@/permissions";

export default {
    name: "client-list-table",
    components: {
        CountryFlag
    },
    data() {
        return {
            sortBy: null,
            sortDesc: false
        };
    },
    mounted() {
        // Load all clients (no pagination for flat table)
        Store.dispatch("ClientStore/getClientsList", {
            options: null,
            search: this.searchClientString,
            tab: this.activeTab
        });

        // Add keyboard listener for navigation
        document.addEventListener("keydown", this.handleKeydown);
    },
    destroyed() {
        document.removeEventListener("keydown", this.handleKeydown);
    },
    computed: {
        ...mapState("ClientStore", [
            "searchClientString",
            "loadingClientList",
            "activeClient",
            "clients",
            "totalClientCount",
            "clientTableHeaders",
            "activeTab",
            "tabCounts",
            "selectedRowId",
            "highlightedColumn",
            "infoPanelOpen",
            "isEditMode"
        ]),
        ...mapState(["user"]),
        ...mapGetters("ClientStore", ["clientCount"]),
        ...mapGetters(["hasPermissionForAction"]),
        PermissionsConstants() {
            return PermissionsConstants;
        },
        tableHeaders() {
            return this.clientTableHeaders.map(h => ({
                ...h,
                text: this.t(h.text),
                class: this.highlightedColumn === h.value ? 'highlighted-header' : ''
            }));
        },
        filteredClients() {
            // Backend already filters, just return clients
            return this.clients;
        }
    },
    watch: {
        activeTab() {
            // Reload clients when tab changes
            Store.dispatch("ClientStore/getClientsList", {
                options: null,
                search: this.searchClientString,
                tab: this.activeTab
            });
        }
    },
    methods: {
        ...mapMutations("ClientStore", [
            "setSelectedRowId",
            "setHighlightedColumn",
            "setInfoPanelOpen"
        ]),
        handleRowClick(item, row) {
            this.setSelectedRowId(item.id);

            // Open drawer on single click
            if (this.hasPermissionForAction(PermissionsConstants.CLIENT, PermissionsConstants.VIEW)) {
                Store.dispatch("ClientStore/getClientData", item.id);
                this.setInfoPanelOpen(true);
                // Update URL
                this.$router.push({ name: "client-view", params: { clientId: item.id } }).catch(() => {});
            }
        },
        getRowClass(item) {
            return item.id === this.selectedRowId ? 'selected-row' : '';
        },
        handleHeaderClick(column) {
            // Toggle highlight on header click
            if (this.highlightedColumn === column) {
                this.setHighlightedColumn(null);
            } else {
                this.setHighlightedColumn(column);
            }
        },
        handleKeydown(e) {
            // Only handle when table is focused and no input is active
            if (document.activeElement.tagName === "INPUT" || document.activeElement.tagName === "TEXTAREA") {
                return;
            }

            // Ignore if info panel is in edit mode
            if (this.isEditMode) {
                return;
            }

            const currentIndex = this.filteredClients.findIndex(c => c.id === this.selectedRowId);

            if (e.key === "ArrowUp" && currentIndex > 0) {
                e.preventDefault();
                const newClient = this.filteredClients[currentIndex - 1];
                this.setSelectedRowId(newClient.id);
                Store.dispatch("ClientStore/getClientData", newClient.id);
                this.scrollToRow(currentIndex - 1);
                // Update URL if panel is open
                if (this.infoPanelOpen) {
                    this.$router.push({ name: "client-view", params: { clientId: newClient.id } }).catch(() => {});
                }
            } else if (e.key === "ArrowDown" && currentIndex < this.filteredClients.length - 1) {
                e.preventDefault();
                const newClient = this.filteredClients[currentIndex + 1];
                this.setSelectedRowId(newClient.id);
                Store.dispatch("ClientStore/getClientData", newClient.id);
                this.scrollToRow(currentIndex + 1);
                // Update URL if panel is open
                if (this.infoPanelOpen) {
                    this.$router.push({ name: "client-view", params: { clientId: newClient.id } }).catch(() => {});
                }
            } else if (e.key === "Enter" && this.selectedRowId) {
                e.preventDefault();
                Store.dispatch("ClientStore/getClientData", this.selectedRowId);
                this.setInfoPanelOpen(true);
                this.$router.push({ name: "client-view", params: { clientId: this.selectedRowId } }).catch(() => {});
            }
        },
        scrollToRow(index) {
            // Scroll table to make selected row visible
            const table = this.$refs.dataTable;
            if (table && table.$el) {
                const rows = table.$el.querySelectorAll('tbody tr');
                if (rows[index]) {
                    rows[index].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                }
            }
        }
    }
};
</script>

<style scoped>
.client-table {
    height: calc(100vh - 160px);
}

.id-cell {
    color: #888;
    font-size: 12px;
}

.selected-row {
    background-color: #ffebee !important;
}

.selected-row:hover {
    background-color: #ffcdd2 !important;
}

.highlighted-cell {
    background-color: #fff3e0;
}

.highlighted-header {
    background-color: #fff3e0 !important;
}

.tag-chip {
    font-size: 10px;
    color: #666;
    background-color: #e0e0e0;
    padding: 1px 6px;
    border-radius: 10px;
    margin-right: 4px;
    display: inline-block;
}

::v-deep .v-data-table__wrapper {
    height: 100%;
    overflow-y: auto;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr {
    cursor: pointer;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr:hover {
    background-color: rgba(0, 0, 0, 0.04);
}

::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
    font-size: 12px;
    white-space: nowrap;
    padding: 0 12px !important;
    height: 32px;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
    font-size: 13px;
    padding: 4px 12px !important;
}
</style>
